import type { Cadeia, IdPalavra, Orientacao } from '../tipos';
import type { Semente } from '../aleatorio/rng';
import { derivar, inteiroAte } from '../aleatorio/rng';
import { ehHifen } from '../lexico/alfabeto';
import { caracteresCanonicos, paraCanonica } from '../lexico/normalizacao';
import { temDitongo, temHiato } from '../lexico/silabas';
import { celulaNoIndice } from '../tabuleiro/coordenadas';
import { construirGrade } from '../tabuleiro/grade';
import { palavrasDoJogador } from '../termo/partida';

/**
 * Regras do modo Palavredo (§22.7).
 *
 * Arcade de níveis sem fim. Cada nível é um desafio sobre o mesmo tabuleiro:
 * fechar circuitos, escrever palavras com certa terminação, com certo começo,
 * com certa letra. Cumpridos os objetivos, o nível sobe e o desafio seguinte
 * entra — e a cadeia continua de pé.
 *
 * **A cadeia não é apagada entre níveis**, e é essa a decisão que dá o modo. O
 * arcade clássico zera a tela a cada fase; aqui o tabuleiro é o trabalho do
 * jogador (§22.6 já dizia isso de outra forma), e jogá-lo fora a cada dois
 * minutos cobraria duas vezes pela mesma coisa. O que muda de nível para nível
 * é a cobrança, não o mundo.
 *
 * **A cada três níveis, porém, o tabuleiro fecha.** É a fase: o marco que o
 * arcade precisa ter para não virar esteira. Não é limitação de máquina —
 * medido, um tabuleiro de quinhentas jogadas ainda digita em quatro
 * milissegundos por tecla, e o palco já virtualiza (§16). É que depois de cem
 * níveis a cadeia é uma mancha que se percorre com a câmera, sem centro e sem
 * silhueta, e nunca se chega a lugar nenhum.
 *
 * O fim de fase é também onde o download do tabuleiro (§7.5) finalmente tem
 * hora: escondido no painel do `i`, ele é um item de menu; oferecido no
 * instante em que o tabuleiro vai embora, é o troféu da fase.
 *
 * **Nada de progresso é guardado.** O estado carrega dois números — em que
 * nível se está e a partir de que ponto da cadeia ele conta — e todo o resto é
 * derivado de `(cadeia, nivelDesde, desafio)`. É a mesma escolha da ADR-0009:
 * contador guardado é contador que pode divergir do tabuleiro, e aqui o
 * tabuleiro é a única fonte de verdade.
 *
 * O desafio de cada nível sai da semente da sessão (ADR-0004): a mesma partida
 * propõe sempre os mesmos níveis, na mesma ordem, e o teste que a reexecuta
 * vê o que o jogador viu.
 */

/* ── O que um nível pede ──────────────────────────────────────────────── */

/**
 * Um objetivo do nível. Todos têm `alvo` — quantas vezes é preciso cumprir —
 * porque "fechar um circuito" e "fechar quatro" são o mesmo desafio em
 * intensidades diferentes, e tratá-los como coisas distintas dobraria o código
 * da contagem.
 */
export type Objetivo =
  /** Retângulo fechado por duas bases e duas pontes. */
  | { readonly tipo: 'circuito'; readonly alvo: number }
  | { readonly tipo: 'terminacao'; readonly alvo: number; readonly parte: string }
  | { readonly tipo: 'inicio'; readonly alvo: number; readonly parte: string }
  | { readonly tipo: 'contem'; readonly alvo: number; readonly parte: string }
  /** Qualquer letra aparecendo duas vezes: `CASA`, `OVO`, `PAPEL`. */
  | { readonly tipo: 'dobrada'; readonly alvo: number }
  /** Uma letra escolhida, duas vezes: dois `A`, dois `R`. */
  | { readonly tipo: 'dobrada-de'; readonly alvo: number; readonly parte: string }
  | { readonly tipo: 'ditongo'; readonly alvo: number }
  | { readonly tipo: 'hiato'; readonly alvo: number };

export type TipoObjetivo = Objetivo['tipo'];

export interface Desafio {
  readonly nivel: number;
  readonly objetivos: readonly Objetivo[];
}

export interface ProgressoObjetivo {
  readonly objetivo: Objetivo;
  readonly feito: number;
  readonly cumprido: boolean;
}

/**
 * Os pedaços de palavra que o modo pede, **do mais fácil de encaixar para o
 * mais difícil**.
 *
 * A ordem não é enfeite: os primeiros de cada lista são o repertório dos
 * níveis iniciais. Dificuldade aqui não é raridade no dicionário, é dificuldade
 * de *caber* — com a âncora mandando na letra obrigatória, uma palavra em
 * MENTE tem nove letras para acomodar, e pedi-la no nível 1 é outra coisa que
 * pedir uma em ADO.
 *
 * Nenhum deles é raro: o desafio é encaixar, não lembrar de uma exceção.
 */
const TERMINACOES = ['ADO', 'ADA', 'ÃO', 'OSO', 'EIRO', 'ISTA', 'INHO', 'MENTE'] as const;
const INICIOS = ['RE', 'DES', 'CON', 'EN', 'TRA', 'PRE', 'SUB', 'PRO'] as const;
const ESPECIAIS = ['Ã', 'Ç', 'É', 'Á', 'Õ', 'Ê', 'Ó'] as const;

/**
 * Letras que valem como "dobrada", da mais fácil para a mais rara.
 *
 * A ordem saiu de contar o corpus, e não de intuição: dois `A` cabem em 37% das
 * palavras jogáveis, dois `L` em 1,6%. `Ç` e `Ã` ficaram de fora — o corpus tem
 * 21 e 3 palavras com eles dobrados, o que faria um nível impossível.
 */
const DOBRADAS = ['A', 'E', 'R', 'I', 'O', 'S', 'T', 'N', 'C', 'M', 'L'] as const;

/**
 * Quantos itens de cada lista os primeiros níveis usam.
 *
 * É a calibragem do modo, e vive aqui como número com nome justamente para ser
 * mexida: se o nível 2 se mostrar duro demais em teste com gente, o ajuste é
 * este número, não uma reescrita.
 */
const FACEIS = 4;

/**
 * Nível em que cada tipo entra no sorteio.
 *
 * O circuito é de outra ordem de dificuldade: exige duas palavras suas
 * cruzando as mesmas duas pontes, o que é planejamento espacial e não memória
 * de vocabulário. Pedi-lo no nível 1 seria abrir o modo com a sua parte mais
 * dura, e quem não entende o que se pede desiste antes de aprender.
 */
const ESTREIA: Record<TipoObjetivo, number> = {
  terminacao: 1,
  inicio: 1,
  // Uma letra repetida cabe em 85% do corpus: é o objetivo de entrada, o que
  // ensina a ler a própria palavra sem cobrar quase nada.
  dobrada: 1,
  contem: 2,
  ditongo: 2,
  circuito: 3,
  'dobrada-de': 3,
  // Hiato só na segunda faixa: 9,6% do corpus, e ainda pede saber o que é.
  hiato: 6,
};

/** A partir daqui o nível cobra dois objetivos ao mesmo tempo. */
const NIVEL_COMBINADO = 4;

/**
 * Níveis que cabem numa fase (§22.7).
 *
 * Três. Era cinco, e cinco era longe demais: com os alvos de hoje a fase pedia
 * de vinte a trinta jogadas, o tabuleiro chegava ao fim já ilegível e o marco
 * que deveria pontuar a partida virava a própria esteira que ele existe para
 * quebrar. É um número com nome justamente para ser mexido quando alguém jogar
 * o bastante para saber.
 *
 * Mexer nele mexe só no **comprimento** da partida: quando endurecer é assunto
 * de `NIVEIS_POR_FAIXA`, que conta níveis por conta própria.
 */
export const NIVEIS_POR_FASE = 3;

/**
 * Níveis que cabem num degrau de dificuldade (§22.7).
 *
 * Cinco, e a fase tem três: os dois ritmos são deliberadamente diferentes.
 * Já foram o mesmo — a faixa era `faseDoNivel(nivel) - 1` —, e aí encurtar a
 * fase de cinco para três encurtou junto o degrau, endurecendo a partida
 * justamente na mudança que existia para aliviá-la. São duas perguntas
 * distintas — *de quanto em quanto o tabuleiro fecha?* e *de quanto em quanto
 * a cobrança sobe?* — e agora têm duas respostas que se mexem sozinhas.
 */
const NIVEIS_POR_FAIXA = 5;

/**
 * A fase a que um nível pertence, começando em 1.
 *
 * **Cuidado com o nome:** `Fase` no núcleo já é o estado do autômato
 * (`carregando`, `digitando`, `fim`). Esta é outra coisa — o macrogrupo de
 * níveis do arcade — e por isso não existe tipo `Fase` aqui nem campo `fase`
 * no estado. Ela é derivada do nível, sempre, e nunca guardada.
 */
export function faseDoNivel(nivel: number): number {
  return Math.floor((nivel - 1) / NIVEIS_POR_FASE) + 1;
}

/** Posição do nível dentro da fase, de 1 a `NIVEIS_POR_FASE`. */
export function nivelNaFase(nivel: number): number {
  return ((nivel - 1) % NIVEIS_POR_FASE) + 1;
}

/** É o último nível da fase? Cumpri-lo fecha o tabuleiro e abre a fase seguinte. */
export function fechaAFase(nivel: number): boolean {
  return nivelNaFase(nivel) === NIVEIS_POR_FASE;
}

/**
 * Degraus de dificuldade, contados em níveis.
 *
 * O teto é a quarta faixa: sem ele o alvo cresceria para sempre, e lá pelo
 * nível cem o modo pediria trinta palavras com hiato.
 */
export function faixaDoNivel(nivel: number): number {
  return Math.min(3, Math.floor((nivel - 1) / NIVEIS_POR_FAIXA));
}

/**
 * Quantas vezes cada objetivo pede, antes do degrau da faixa.
 *
 * Os números saíram de contar quantas palavras do corpus servem a cada um: o
 * que cabe em 85% das palavras pode pedir três de uma vez, o que cabe em 10%
 * pede uma. Sem essa medida, "escreva 3 palavras com hiato" e "escreva 3
 * palavras com letra repetida" pareceriam o mesmo pedido no código e seriam
 * outra coisa na mão de quem joga.
 */
const ALVO_BASE: Record<TipoObjetivo, number> = {
  dobrada: 3,
  terminacao: 2,
  inicio: 2,
  contem: 2,
  ditongo: 2,
  'dobrada-de': 1,
  hiato: 1,
  circuito: 1,
};

/**
 * Quantas vezes o objetivo precisa ser cumprido nesta faixa.
 *
 * O circuito cresce pela metade do ritmo dos outros: quatro circuitos num nível
 * não são quatro vezes mais difíceis, são um tabuleiro inteiro planejado — e o
 * modo ficaria parado no mesmo nível por meia hora.
 */
function alvoDe(tipo: TipoObjetivo, faixa: number): number {
  const base = ALVO_BASE[tipo];
  return base + (tipo === 'circuito' ? Math.floor(faixa / 2) : faixa);
}

/**
 * O desafio de um nível, derivado da semente da sessão (ADR-0004).
 *
 * Função pura de `(semente, nivel)`: não há sorteio guardado, e por isso a
 * sessão salva não precisa carregar o desafio — basta o número do nível.
 */
export function desafioDoNivel(semente: Semente, nivel: number): Desafio {
  const faixa = faixaDoNivel(nivel);
  const disponiveis = (Object.keys(ESTREIA) as TipoObjetivo[])
    .filter((tipo) => nivel >= ESTREIA[tipo])
    // Ordem fixa: `Object.keys` já é estável, mas depender disso para uma
    // escolha determinística seria confiar num detalhe do motor.
    .sort();

  const quantos = nivel >= NIVEL_COMBINADO && disponiveis.length > 1 ? 2 : 1;
  const objetivos: Objetivo[] = [];
  const restantes = [...disponiveis];

  for (let i = 0; i < quantos; i += 1) {
    const semeado = derivar(semente, 'palavredo', nivel, i);
    const escolhido = restantes[inteiroAte(semeado, restantes.length)];
    if (escolhido === undefined) break;
    // Sem reposição: dois objetivos do mesmo tipo no mesmo nível seriam um só
    // com o alvo somado, e o jogador leria duas linhas dizendo a mesma coisa.
    restantes.splice(restantes.indexOf(escolhido), 1);
    objetivos.push(montarObjetivo(escolhido, alvoDe(escolhido, faixa), nivel, semeado));
  }

  return { nivel, objetivos };
}

/** Os tipos que escolhem um pedaço de palavra; o resto não tem `parte`. */
const REPERTORIO: Partial<Record<TipoObjetivo, readonly string[]>> = {
  terminacao: TERMINACOES,
  inicio: INICIOS,
  contem: ESPECIAIS,
  'dobrada-de': DOBRADAS,
};

function montarObjetivo(
  tipo: TipoObjetivo,
  alvo: number,
  nivel: number,
  semente: Semente,
): Objetivo {
  const completa = REPERTORIO[tipo];
  if (completa === undefined) return { tipo, alvo } as Objetivo;

  // A faixa de estreia joga só com o repertório fácil; da segunda em diante,
  // com a lista inteira. É o mesmo degrau do alvo, e de propósito: repertório e
  // alvo são os dois lados da mesma cobrança, e separá-los poria dois relógios
  // de dificuldade a correr em ritmos diferentes.
  const lista = faixaDoNivel(nivel) === 0 ? completa.slice(0, FACEIS) : completa;
  const parte = lista[inteiroAte(derivar(semente, tipo), lista.length)] ?? completa[0];
  return { tipo, alvo, parte: paraCanonica(parte ?? '') } as Objetivo;
}

/* ── Circuitos ────────────────────────────────────────────────────────── */

/**
 * Quantos retângulos fechados o tabuleiro tem.
 *
 * Um circuito é duas horizontais atravessando as **mesmas duas** verticais: os
 * quatro cruzamentos são os cantos, e os trechos de palavra entre eles são os
 * lados. Como as palavras são contíguas, não é preciso verificar os lados —
 * basta achar o par.
 *
 * Em teoria de grafos isto é contar ciclos de tamanho quatro no grafo bipartido
 * de cruzamentos, e a conta é a de sempre: para cada par de verticais, se `k`
 * horizontais atravessam as duas, há `k·(k−1)/2` retângulos ali.
 *
 * Só as horizontais são varridas, e não todos os pares de palavras: no
 * Entreletras a palavra-base é sempre horizontal e a ponte sempre vertical,
 * então o grafo já nasce bipartido.
 */
export function circuitos(cadeia: Cadeia): number {
  const grade = construirGrade(cadeia);

  const orientacaoDe = new Map<IdPalavra, Orientacao>();
  for (const palavra of cadeia.palavras) orientacaoDe.set(palavra.id, palavra.orientacao);

  // Par de verticais → quantas horizontais atravessam as duas.
  const pares = new Map<string, number>();

  for (const palavra of cadeia.palavras) {
    if (palavra.orientacao !== 'horizontal') continue;

    const verticais = new Set<IdPalavra>();
    const letras = caracteresCanonicos(palavra.texto);
    for (let i = 0; i < letras.length; i += 1) {
      const celula = celulaNoIndice(palavra.origem, palavra.orientacao, i);
      for (const id of grade.palavrasEm(celula)) {
        if (orientacaoDe.get(id) === 'vertical') verticais.add(id);
      }
    }

    const lista = [...verticais].sort();
    for (let a = 0; a < lista.length; a += 1) {
      for (let b = a + 1; b < lista.length; b += 1) {
        const chave = `${lista[a]}|${lista[b]}`;
        pares.set(chave, (pares.get(chave) ?? 0) + 1);
      }
    }
  }

  let total = 0;
  for (const k of pares.values()) total += (k * (k - 1)) / 2;
  return total;
}

/* ── Progresso ────────────────────────────────────────────────────────── */

/**
 * Quantas palavras do jogador, deste nível para cá, cumprem o objetivo.
 *
 * Conta só as palavras-base: a vertical veio do sistema, e premiar o jogador
 * por uma terminação que ele não escolheu seria pagá-lo pelo acaso da
 * sugestão — a mesma razão que faz `palavrasEscritas` do Nitro contar metade
 * da cadeia.
 *
 * O circuito é a exceção, e tem de ser: ele é feito de duas bases **e** duas
 * pontes, e nenhuma metade fecha um retângulo sozinha. Ali o que conta é o
 * tabuleiro inteiro, descontado o que já estava fechado quando o nível
 * começou.
 */
function contar(cadeia: Cadeia, desde: number, objetivo: Objetivo): number {
  if (objetivo.tipo === 'circuito') {
    const antes: Cadeia = { palavras: cadeia.palavras.slice(0, desde) };
    return circuitos(cadeia) - circuitos(antes);
  }

  const novas = palavrasDoJogador(cadeia).filter((p) => p.turno >= desde);
  return novas.filter((p) => palavraServe(paraCanonica(p.texto), objetivo)).length;
}

/**
 * A palavra serve a este objetivo?
 *
 * Exportada para que o teste de corpus possa perguntar, para cada desafio que o
 * modo sabe propor, quantas palavras do dicionário de produção o servem. Foi
 * essa pergunta que descartou o `hífen`: zero palavras em 366 mil.
 *
 * O circuito não passa por aqui — ele é do tabuleiro, não da palavra.
 */
export function palavraServe(texto: string, objetivo: Objetivo): boolean {
  switch (objetivo.tipo) {
    case 'circuito':
      return false;
    case 'terminacao':
      return texto.endsWith(objetivo.parte);
    case 'inicio':
      return texto.startsWith(objetivo.parte);
    case 'contem':
      return texto.includes(objetivo.parte);
    case 'dobrada':
      return temLetraDobrada(texto);
    case 'dobrada-de':
      return vezesDaLetra(texto, objetivo.parte) >= 2;
    case 'ditongo':
      return temDitongo(texto);
    case 'hiato':
      return temHiato(texto);
  }
}

/**
 * Alguma letra aparece duas vezes?
 *
 * Letra, e não caractere: o hífen fica de fora, senão `GUARDA-CHUVA` contaria
 * pelo traço. Acentuada é outra letra — §4.3 preserva diacríticos, e `Á` não
 * dobra com `A`, do mesmo jeito que não satisfaz uma âncora `A` (T05-06).
 */
function temLetraDobrada(texto: string): boolean {
  const vistas = new Set<string>();
  for (const letra of caracteresCanonicos(texto)) {
    if (ehHifen(letra)) continue;
    if (vistas.has(letra)) return true;
    vistas.add(letra);
  }
  return false;
}

function vezesDaLetra(texto: string, letra: string): number {
  return caracteresCanonicos(texto).filter((c) => c === letra).length;
}

export function progressoDoNivel(
  cadeia: Cadeia,
  desde: number,
  desafio: Desafio,
): readonly ProgressoObjetivo[] {
  return desafio.objetivos.map((objetivo) => {
    const feito = contar(cadeia, desde, objetivo);
    return { objetivo, feito, cumprido: feito >= objetivo.alvo };
  });
}

/** O nível está cumprido? Todos os objetivos, não um deles. */
export function nivelCumprido(progresso: readonly ProgressoObjetivo[]): boolean {
  return progresso.length > 0 && progresso.every((p) => p.cumprido);
}

/**
 * Atalho para o redutor: a cadeia recém-formada cumpre o nível?
 *
 * Existe para que o motor não precise montar desafio e progresso à mão a cada
 * jogada — e para que a regra de subir de nível esteja escrita num lugar só.
 */
export function cumpriuONivel(
  cadeia: Cadeia,
  semente: Semente,
  nivel: number,
  desde: number,
): boolean {
  return nivelCumprido(progressoDoNivel(cadeia, desde, desafioDoNivel(semente, nivel)));
}

/* ── Texto ────────────────────────────────────────────────────────────── */

/**
 * O objetivo em palavras (§7.3).
 *
 * Mora aqui, e não na interface, porque é texto de aceite como o resto das
 * mensagens do jogo: os testes comparam com esta função, não com literais
 * espalhados pelos componentes.
 */
export function rotularObjetivo(objetivo: Objetivo): string {
  const { alvo } = objetivo;
  const uma = alvo === 1;
  const palavras = uma ? 'palavra' : 'palavras';

  switch (objetivo.tipo) {
    case 'circuito':
      return uma ? 'Feche 1 circuito' : `Feche ${alvo} circuitos`;
    case 'terminacao':
      // O particípio concorda com o número: `1 palavra terminadas` é o tipo de
      // erro que passa despercebido no código e salta aos olhos na tela.
      return `Escreva ${alvo} ${palavras} ${uma ? 'terminada' : 'terminadas'} em ${objetivo.parte}`;
    case 'inicio':
      return `Escreva ${alvo} ${palavras} ${uma ? 'começada' : 'começadas'} por ${objetivo.parte}`;
    case 'contem':
      return `Escreva ${alvo} ${palavras} com ${objetivo.parte}`;
    case 'dobrada':
      return `Escreva ${alvo} ${palavras} com uma letra repetida`;
    case 'dobrada-de':
      return `Escreva ${alvo} ${palavras} com dois ${objetivo.parte}`;
    case 'ditongo':
      return `Escreva ${alvo} ${palavras} com ditongo`;
    case 'hiato':
      return `Escreva ${alvo} ${palavras} com hiato`;
  }
}

/** Anúncio do nível que começa agora (§13). */
export function anunciarNivel(desafio: Desafio): string {
  const pedidos = desafio.objetivos.map(rotularObjetivo).join('. ');
  return `Nível ${desafio.nivel}. ${pedidos}`;
}

/** §13: fechar a fase é o marco do modo, e ele se diz por extenso. */
export function anunciarFimDeFase(fase: number): string {
  return `Fase ${fase} completa. O tabuleiro fecha aqui.`;
}

export function tituloDaFase(fase: number): string {
  return `Fase ${fase} completa`;
}
